"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

const images = [
  "/images/lab/cafe.webp",
  "/images/lab/middle-2.webp",
  "/images/lab/desk.webp",
];

export default function Slideshow() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (!isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlay]);

  const handleDotClick = useCallback((index: number) => {
    if (index !== currentIndex) {
      setCurrentIndex(index);
    }
  }, [currentIndex]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsAutoPlay(false);

    // Update current index based on scroll position
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const itemWidth = scrollContainerRef.current.offsetWidth;
      const newIndex = Math.round(scrollLeft / itemWidth);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < images.length) {
        setCurrentIndex(newIndex);
      }
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsAutoPlay(true);
    }, 3000);
  }, [currentIndex]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startX = touch.clientX;

    const handleTouchMove = (e: TouchEvent) => {
      const currentTouch = e.touches[0];
      const deltaX = currentTouch.clientX - startX;

      if (Math.abs(deltaX) > 10) {
        handleScroll();
      }
    };

    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  }, [handleScroll]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide md:block"
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {images.map((src, index) => (
          <div
            key={src}
            className="relative flex-shrink-0 w-full h-full snap-center md:absolute md:inset-0"
          >
            <Image
              width={600}
              height={400}
              src={src}
              alt="Space imagery slideshow"
              loading={"lazy"}
              sizes="(max-width: 768px) 100vw, 600px"
              className={`w-full h-full object-left object-cover md:transition-opacity md:duration-1000 ${index === currentIndex ? "md:opacity-100" : "md:opacity-0"
                }`}
            />
          </div>
        ))}
      </div>
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
        {images.map((_, index) => {
          const isActive = index === currentIndex;
          return (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${isActive ? "bg-white" : "bg-white/50"
                }`}
            />
          );
        })}
      </div>
    </div>
  );
}